import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Clock, Calendar, Globe, Award, Tv, MapPin, Info } from "lucide-react";
import { InnerNav } from "../components/InnerNav";
import { MovieActions } from "../components/MovieActions";
import { Discussion } from "../components/Discussion";
import { TrailerGallery } from "../components/TrailerGallery";
import { Reveal, Overline } from "../components/motion";
import { useTranslate } from "../context/TranslateContext";
import { getMovie, getPerson, getSong, locationsForMovie, ecosystemLinks } from "../mock/data";

const hex = (h, a) => {
  const n = h.replace("#", "");
  const r = parseInt(n.slice(0, 2), 16), g = parseInt(n.slice(2, 4), 16), b = parseInt(n.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
};

export default function MovieDetail() {
  const { id } = useParams();
  const { t } = useTranslate();
  const m = getMovie(id);
  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  if (!m) return (<div><InnerNav /><div className="p-20 text-center font-body text-ash">{t("Movie not found.")}</div></div>);

  const accent = m.accent;
  const listenText = `${m.title}. ${m.synopsis}`;

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(180deg, ${hex(accent, 0.20)} 0%, #FBF6EC 62%)` }}>
      <InnerNav />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={m.image} alt="" className="h-full w-full object-cover blur-2xl scale-110" />
        </div>
        <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-6 py-12 md:px-12 md:py-16 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <div className="overflow-hidden rounded-2xl border shadow-2xl" style={{ borderColor: hex(accent, 0.5) }}>
              <img src={m.image} alt={m.title} data-testid="movie-poster" className="aspect-[2/3] w-full object-cover" />
            </div>
          </Reveal>

          <div className="lg:col-span-8">
            <Overline className="!text-[color:var(--a)]" style={{ "--a": accent }}>{m.language} {t("Cinema")}</Overline>
            <h1 data-testid="movie-title" className="mt-3 font-display text-5xl font-semibold leading-none tracking-tight text-cream md:text-7xl">{m.title}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 font-body text-sm text-ash">
              <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" style={{ color: accent }} />{m.year}</span>
              <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" style={{ color: accent }} />{m.runtime}</span>
              <span className="flex items-center gap-1.5"><Globe className="h-4 w-4" style={{ color: accent }} />{t(m.genre)}</span>
              <span className="rounded border border-edge px-2 py-0.5">{m.cert}</span>
            </div>
            <div className="mt-5 flex items-center gap-3">
              <div className="flex items-center gap-1.5 rounded-full px-4 py-2" style={{ background: hex(accent, 0.15) }}>
                <Star className="h-5 w-5 fill-gold text-gold" />
                <span className="font-display text-2xl font-semibold text-cream">{m.rating}</span>
                <span className="font-body text-xs text-ash">/10</span>
              </div>
              
            </div>
            <p data-testid="movie-synopsis" className="mt-6 max-w-2xl font-body text-lg leading-relaxed text-ash">{t(m.synopsis)}</p>
            <div className="mt-8"><MovieActions movieId={m.id} showRating /></div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] space-y-16 px-6 pb-28 md:px-12">
        {/* Trailer & Gallery */}
        <Reveal><TrailerGallery movie={m} accent={accent} /></Reveal>

        {/* Where to watch */}
        <Section title="Where to Watch" icon={Tv} accent={accent}>
          <div className="flex flex-wrap gap-3" data-testid="where-to-watch">
            {m.whereToWatch.map((w) => (
              <div key={w.name} className="flex items-center gap-3 rounded-xl border border-edge bg-surface/50 px-5 py-3">
                <span className="font-body font-medium text-cream">{w.name}</span>
                <span className="rounded-full px-2.5 py-0.5 font-body text-xs" style={{ background: hex(accent, 0.2), color: accent }}>{t(w.type)}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* Cast & crew */}
        <Section title="Cast & Crew" accent={accent}>
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
            {m.cast.map((c) => { const p = getPerson(c.p); return (
              <PersonChip key={c.p} p={p} sub={`${t("as")} ${c.as}`} />
            ); })}
            {Object.entries(m.crew).map(([role, pid]) => { const p = getPerson(pid); return (
              <PersonChip key={role + pid} p={p} sub={t(role)} />
            ); })}
          </div>
        </Section>

        {/* Songs */}
        {m.songs.length > 0 && (
          <Section title="Soundtrack" accent={accent}>
            <div className="rounded-2xl border border-edge bg-surface/40">
              {m.songs.map((sid) => { const s = getSong(sid); const singer = getPerson(s.singer); const comp = getPerson(s.composer); return (
                <div key={sid} data-testid={`ost-${sid}`} className="flex items-center gap-4 border-b border-edge px-5 py-4 last:border-0">
                  <div className="min-w-0 flex-1">
                    <Link to={`/song/${sid}`} className="font-body text-cream hover:text-gold">{s.title}</Link>
                    <div className="truncate font-body text-sm text-ash">
                      {singer && <Link to={`/person/${singer.id}`} className="hover:text-gold">{singer.name}</Link>}
                      {comp && <> · {t("Music")}: <Link to={`/person/${comp.id}`} className="hover:text-gold">{comp.name}</Link></>}
                    </div>
                  </div>
                  <span className="font-body text-sm text-ash">{t(s.mood)}</span>
                  <span className="font-body text-sm text-ash">{s.duration}</span>
                </div>
              ); })}
            </div>
          </Section>
        )}

        {/* Reviews (multi-dimensional) */}
        <Section title="Critic Scorecard" accent={accent}>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <div className="space-y-4" data-testid="review-dims">
              {Object.entries(m.reviewDims).map(([dim, val]) => (
                <div key={dim}>
                  <div className="mb-1 flex justify-between font-body text-sm">
                    <span className="text-cream">{t(dim)}</span><span className="text-ash">{val}/10</span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-edge">
                    <motion.div initial={{ width: 0 }} whileInView={{ width: `${val * 10}%` }} viewport={{ once: true }}
                      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="h-full rounded-full" style={{ background: accent }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="rounded-2xl border border-edge bg-surface/40 p-6">
              <Overline>Box Office</Overline>
              <div className="mt-4 grid grid-cols-2 gap-4" data-testid="box-office">
                {Object.entries(m.boxOffice).filter(([k]) => k !== "status").map(([k, v]) => (
                  <div key={k}>
                    <div className="font-body text-xs uppercase tracking-wider text-ash">{t(k)}</div>
                    <div className="font-display text-2xl text-cream">{v}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-edge px-3 py-1 font-body text-xs text-ash">
                <Info className="h-3.5 w-3.5" /> {t("Figures")}: {t(m.boxOffice.status)}
              </div>
            </div>
          </div>
        </Section>

        {/* Awards & Trivia */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <Section title="Awards" icon={Award} accent={accent}>
            <ul className="space-y-2">
              {m.awards.map((a) => (<li key={a} className="flex items-start gap-2 font-body text-ash"><Award className="mt-1 h-4 w-4 shrink-0" style={{ color: accent }} />{t(a)}</li>))}
            </ul>
          </Section>
          <Section title="Trivia" accent={accent}>
            <ul className="space-y-2">
              {m.trivia.map((tr) => (<li key={tr} className="font-body text-ash">— {t(tr)}</li>))}
            </ul>
          </Section>
        </div>

        {/* Filming Locations */}
        {locationsForMovie(m.id).length > 0 && (
          <Section title="Filming Locations" icon={MapPin} accent={accent}>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="filming-locations">
              {locationsForMovie(m.id).map((loc, i) => (
                <div key={i} className="rounded-2xl border border-edge bg-surface/40 p-6">
                  <div className="flex items-center gap-2"><MapPin className="h-5 w-5" style={{ color: accent }} />
                    <span className="font-display text-2xl text-cream">{loc.name}</span></div>
                  <div className="mt-1 font-body text-sm text-ash">{loc.city}, {loc.state}</div>
                  <p className="mt-2 font-body text-ash">{t(loc.note)}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {ecosystemLinks(loc.state).map((l, j) => (
                      <a key={j} href={l.url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-full border border-edge bg-surface px-3 py-1.5 font-body text-xs text-cream transition-colors hover:border-gold hover:text-gold">
                        {t(l.label)}
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <Link to="/locations" data-testid="all-locations-link" className="mt-4 inline-block font-body text-sm text-gold hover:underline">{t("Explore all film locations")} →</Link>
          </Section>
        )}

        {/* Related */}
        <Section title="Related Films" accent={accent}>
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
            {m.related.map((rid) => { const r = getMovie(rid); return (
              <Link key={rid} to={`/movie/${rid}`} data-testid={`related-${rid}`}>
                <motion.div whileHover={{ y: -6 }} className="group overflow-hidden rounded-xl border border-edge">
                  <div className="aspect-[2/3] overflow-hidden">
                    <img src={r.image} alt={r.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-3">
                    <div className="font-display text-lg text-cream">{r.title}</div>
                    <div className="font-body text-xs text-ash">{r.language} · {r.year}</div>
                  </div>
                </motion.div>
              </Link>
            ); })}
          </div>
        </Section>
      </div>
      <div className="border-t border-edge bg-paper">
        <Discussion entityType="movie" entityId={m.id} title="Discussion" />
      </div>
    </div>
  );
}

const Section = ({ title, icon: Icon, accent, children }) => {
  const { t } = useTranslate();
  return (
    <Reveal>
      <div className="mb-6 flex items-center gap-3">
        {Icon && <Icon className="h-5 w-5" style={{ color: accent }} />}
        <h2 className="font-display text-3xl font-semibold text-cream md:text-4xl">{t(title)}</h2>
        <div className="h-px flex-1" style={{ background: hex(accent, 0.3) }} />
      </div>
      {children}
    </Reveal>
  );
};

const PersonChip = ({ p, sub }) => {
  if (!p) return null;
  return (
    <Link to={`/person/${p.id}`} data-testid={`cast-${p.id}`} className="group text-center">
      <div className="mx-auto aspect-square w-full overflow-hidden rounded-full border border-edge">
        <img src={p.image} alt={p.name} className="h-full w-full object-cover grayscale transition-all duration-500 group-hover:grayscale-0" />
      </div>
      <div className="mt-2 font-body text-sm text-cream group-hover:text-gold">{p.name}</div>
      <div className="font-body text-xs text-ash">{sub}</div>
    </Link>
  );
};
